import React, { useEffect, useMemo, useState } from 'react';
import { Card, Group, Title, Text, Switch, NumberInput, Button, Stack, Divider, Select, Tooltip } from '@mantine/core';
import ThemeSwitcher from '../components/common/ThemeSwitcher';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { geocodeLocations } from '../services/dataService';

// Local storage keys
const STORAGE_KEYS = {
  thresholds: 'tt.summary.thresholds',
  theme: 'tt.theme',
  locale: 'i18nextLng',
  weatherPlace: 'tt_weather_place',
  testMode: 'tt.testMode',
} as const;

type SummaryThresholds = {
  batteryLow: number; // %
  inactiveDays: number; // days without communication considered stale
  batteryMedium: number; // % medium threshold above low
  inactiveDaysWarn: number; // days without communication considered warning
};

const defaultThresholds: SummaryThresholds = {
  batteryLow: 20,
  inactiveDays: 7,
  batteryMedium: 40,
  inactiveDaysWarn: 3,
};

const SettingsPage: React.FC = () => {
  const { t, i18n: _i18n } = useTranslation();
  const [thresholds, setThresholds] = useState<SummaryThresholds>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.thresholds);
      return raw ? { ...defaultThresholds, ...JSON.parse(raw) } : defaultThresholds;
    } catch {
      return defaultThresholds;
    }
  });

  // Note: i18n language is managed by LanguageSwitcher component; no local state needed here.

  // Test mode toggle (persisted, not shown in UI, default: false)
  const [testMode, setTestMode] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEYS.testMode);
      if (v === 'true') return true;
      if (v === 'false') return false;
    } catch {
      /* ignore */
    }
    // Default to false (no URL param fallback)
    return false;
  });

  // Weather location state
  const [weatherSearch, setWeatherSearch] = useState<string>('');
  const [weatherOptions, setWeatherOptions] = useState<{ value: string; label: string; lat: number; lon: number }[]>([]);
  const [weatherPlace, setWeatherPlace] = useState<{ label: string; latitude: number; longitude: number } | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.weatherPlace);
      if (!raw) return null;
      const o = JSON.parse(raw);
      if (o && typeof o === 'object' && 'label' in o && 'latitude' in o && 'longitude' in o) {
        return o as { label: string; latitude: number; longitude: number };
      }
      return null;
    } catch {
      return null;
    }
  });

  // Ensure the saved place appears as an option so the Select can show it
  useEffect(() => {
    if (weatherPlace) {
      const saved = { value: `${weatherPlace.latitude},${weatherPlace.longitude}`, label: weatherPlace.label, lat: weatherPlace.latitude, lon: weatherPlace.longitude };
      setWeatherOptions((opts) => {
        const merged = [saved, ...opts];
        const uniq = Array.from(new Map(merged.map((o) => [o.value, o])).values());
        return uniq;
      });
    }
  }, [weatherPlace]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.thresholds, JSON.stringify(thresholds));
    } catch {
      /* ignore */
    }
  }, [thresholds]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.testMode, String(testMode));
    } catch {
      /* ignore */
    }
  }, [testMode]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.weatherPlace, JSON.stringify(weatherPlace));
    } catch {
      /* ignore */
    }
  }, [weatherPlace]);

  const canReset = useMemo(() =>
    thresholds.batteryLow !== defaultThresholds.batteryLow ||
    thresholds.inactiveDays !== defaultThresholds.inactiveDays ||
    thresholds.batteryMedium !== defaultThresholds.batteryMedium ||
    thresholds.inactiveDaysWarn !== defaultThresholds.inactiveDaysWarn,
  [thresholds]);

  return (
    <Stack gap="lg">
      <Group gap="md" mb="xs" style={{ color: 'var(--tt-dark)' }}>
        <Title order={2} style={{ fontWeight: 300 }}>{t('navigation.settings') || 'Settings'}</Title>
      </Group>
      <Text c="dimmed">{t('settings.description') || 'Configure appearance, language, and thresholds for the dashboard.'}</Text>

      <Card withBorder>
        <Title order={4} mb="sm">{t('settings.appearance') || 'Appearance'}</Title>
        <Stack gap="sm">
          <div>
            <Text fw={500} mb={4}>{t('common.theme') || 'Theme'}</Text>
            <ThemeSwitcher />
          </div>
          <Divider my="xs" />
          <div>
            <Text fw={500} mb={4}>{t('common.language') || 'Language'}</Text>
            <LanguageSwitcher />
          </div>
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={4} mb="sm">{t('settingsWeather.title') || 'Weather'}</Title>
        <Stack gap="sm">
          <div>
            <Tooltip label={t('settingsWeather.locationTooltip') || 'Location used for weather overlays on the Data page'} withArrow>
              <Text fw={500} mb={4} style={{ display: 'inline-block' }}>{t('settingsWeather.location') || 'Location'}</Text>
            </Tooltip>
            <Select
              placeholder={t('settingsWeather.searchPlace') || 'Search place'}
              data={weatherOptions}
              value={weatherPlace ? `${weatherPlace.latitude},${weatherPlace.longitude}` : null}
              searchValue={weatherSearch}
              onSearchChange={async (s) => {
                setWeatherSearch(s);
                if (!s || s.trim().length < 2) return;
                try {
                  const res = await geocodeLocations(s, 8);
                  const list = res.map((p) => ({
                    value: `${p.latitude},${p.longitude}`,
                    label: `${p.name}${p.admin1 ? ', ' + p.admin1 : ''}${p.country ? ', ' + p.country : ''}`,
                    lat: p.latitude,
                    lon: p.longitude,
                  }));
                  // Deduplicate by value
                  let uniq = Array.from(new Map(list.map((o) => [o.value, o])).values());
                  // Ensure persisted selection is present, without duplicates
                  if (weatherPlace) {
                    const saved = { value: `${weatherPlace.latitude},${weatherPlace.longitude}`, label: weatherPlace.label, lat: weatherPlace.latitude, lon: weatherPlace.longitude };
                    uniq = Array.from(new Map([saved, ...uniq].map((o) => [o.value, o])).values());
                  }
                  setWeatherOptions(uniq);
                } catch {
                  /* ignore */
                }
              }}
              onChange={(val) => {
                const sel = weatherOptions.find((o) => o.value === val);
                if (sel) setWeatherPlace({ label: sel.label, latitude: sel.lat, longitude: sel.lon });
              }}
              searchable
              clearable
              comboboxProps={{ withinPortal: true }}
            />
            {weatherPlace && (
              <Text size="sm" c="dimmed" mt={6}>{weatherPlace.label}</Text>
            )}
          </div>
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={4} mb="sm">{t('settings.thresholds') || 'Thresholds'}</Title>
        <Text c="dimmed" mb="sm">{t('settings.thresholds_hint') || 'These thresholds are used by the Summary page to highlight items.'}</Text>

        <Stack gap="sm">
          <div>
            <Title order={5} mb={4}>{t('settings.battery_section') || 'Battery thresholds'}</Title>
            <Text c="dimmed" mb={8}>{t('settings.battery_hint') || 'Define medium and low battery levels (%)'}</Text>
            <Group grow>
              <NumberInput
                label={t('settings.battery_medium') || 'Battery medium (%)'}
                min={0}
                max={100}
                value={thresholds.batteryMedium}
                onChange={(v) => setThresholds((s) => ({ ...s, batteryMedium: typeof v === 'number' ? v : s.batteryMedium }))}
              />
              <NumberInput
                label={t('settings.battery_low') || 'Battery low (%)'}
                min={0}
                max={100}
                value={thresholds.batteryLow}
                onChange={(v) => setThresholds((s) => ({ ...s, batteryLow: typeof v === 'number' ? v : s.batteryLow }))}
              />
            </Group>
          </div>

          <Divider my="xs" />

          <div>
            <Title order={5} mb={4}>{t('settings.activity_section') || 'Module Activity thresholds'}</Title>
            <Text c="dimmed" mb={8}>{t('settings.activity_hint') || 'Define warning and critical inactivity (days) for Module communication'}</Text>
            <Group grow>
              <NumberInput
                label={t('settings.inactive_days_warn') || 'Warning after (days)'}
                min={1}
                max={365}
                value={thresholds.inactiveDaysWarn}
                onChange={(v) => setThresholds((s) => ({ ...s, inactiveDaysWarn: typeof v === 'number' ? v : s.inactiveDaysWarn }))}
              />
              <NumberInput
                label={t('settings.inactive_days') || 'Inactive after (days)'}
                min={1}
                max={365}
                value={thresholds.inactiveDays}
                onChange={(v) => setThresholds((s) => ({ ...s, inactiveDays: typeof v === 'number' ? v : s.inactiveDays }))}
              />
            </Group>
          </div>
        </Stack>

        <Group mt="md">
          <Button variant="default" disabled={!canReset} onClick={() => setThresholds(defaultThresholds)}>
            {t('common.reset') || 'Reset'}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
};

export default SettingsPage;
